import classNames from 'classnames'
import { Swiper as SwiperInterface } from 'swiper'

import { ProductCard } from '@src/components/product-card/product-card.component'
import { getInitialSelectedVariant, navigateToPDP } from '@src/components/product-card/product-card.helpers'
import { IProductCard } from '@src/components/product-card/product-card.types'
import { SvdProductCard } from '@src/components/svd-product-card'
import { isPlSvdCard, mapPlSvdCard, type PlSvdCard } from '@src/components/svd-product-card/map-pl-svd-card'
import { useQuinceRouter as useRouter } from '@src/modules/navigation'
import { SkeletonPlaceholders } from '@src/quince-components/src/atoms/skeletonPlaceholders'
import { TrackingMetaData } from '@src/quince-site-common/src/domains/CustomSection'
import useDeviceInfo, { DEVICE_TYPES } from '@src/quince-site-common/src/hooks/useDeviceInfo'
import { RECOMMENDATION_TRACKING_KEYS } from '@src/quince-site-common/src/organisms/appLayout/molecules/cartPopUp/molecules/cartPopupTwo/molecules/recommendedForYou/molecules/product-list-carousel/product-list-carousel.constants'
import { MAX_NUMBER_OF_COLOR_OPTIONS } from '@src/quince-site-common/src/utils/constants'
import getMaxWidthInPx from '@src/quince-site-common/src/utils/getMaxWidthInPx'
import makeChunks from '@src/quince-site-common/src/utils/makeChunks'
import { shouldUseSvdCard } from '@src/utils/should-use-svd-card'

import Swiper from '../../atoms/swiper'
import { DOTS_POSITION } from '../../atoms/swiper/Swiper'
import { TrackConfig } from '../../hocs/withTracking'

import InfiniteScrollProductList from './infiniteScrollProductList'
import styles from './productListSectionTwo.module.scss'

const PRODUCT_LIST_SECTION_TWO_ID = 'productListSectionTwo'

interface ProductListContentProps {
  productCards: IProductCard[]
  infiniteScroll?: boolean
  swiperClassName?: string
  gridColCount?: number
  tracker?: string
  id?: string
  productCardDesktopClassName?: string
  productCardExpClassName?: string
  productCardMobileClassName?: string
  wishlistIconOverrideClass?: string
  maxWidth?: number
  hideProductReviews?: boolean
  hideProductOptions?: boolean
  hideWishlistIcon?: boolean
  productClickTrackConfig?: TrackConfig
  hideProductName?: boolean
  productCardLinkClass?: string
  hideTags?: boolean
  skipTagRefresh?: boolean
  wishlistTrackConfig?: (productCard: IProductCard, idx: number) => Record<string, any>
  additionalProductTrackingContext?: (productCard: IProductCard, idx: number) => Record<string, any>
  productViewTracker?: string
  productPriceClass?: string
  isNewDesign?: boolean
  productCardImageSectionClass?: string
  trackingMetaData?: TrackingMetaData
  carouselArrowClickTracker?: ({ direction }: { direction: string }) => void
  productCardWrapperMobileClassName?: string
  chunkSize?: number
  prevIconClass?: string
  nextIconClass?: string
  afterChange?: (swiperInstance: SwiperInterface) => void
  hideBuyNowIcon?: boolean
  disableImageHoverSwap?: boolean
}

export function ProductListContent({
  productCards,
  infiniteScroll = false,
  swiperClassName,
  gridColCount,
  tracker,
  id,
  productCardDesktopClassName,
  productCardExpClassName,
  productCardMobileClassName,
  wishlistIconOverrideClass,
  maxWidth,
  hideProductReviews,
  hideProductOptions,
  hideWishlistIcon,
  productClickTrackConfig,
  hideProductName,
  productCardLinkClass,
  hideTags,
  skipTagRefresh,
  wishlistTrackConfig = () => ({}),
  additionalProductTrackingContext = () => ({}),
  productViewTracker,
  productPriceClass,
  isNewDesign,
  productCardImageSectionClass,
  trackingMetaData,
  carouselArrowClickTracker,
  productCardWrapperMobileClassName,
  chunkSize,
  prevIconClass,
  nextIconClass,
  afterChange,
  hideBuyNowIcon,
  disableImageHoverSwap,
}: ProductListContentProps) {
  const router = useRouter()
  const deviceInfo = useDeviceInfo()
  const isDesktop = deviceInfo.deviceType === DEVICE_TYPES.DESKTOP
  const isMobile = deviceInfo.deviceType === DEVICE_TYPES.MOBILE
  const productCardChunks = makeChunks(productCards, chunkSize ?? 4)
  const _maxWidth =
    maxWidth ??
    getMaxWidthInPx({
      maxWidthMobileInRem: (isNewDesign ? styles.productCardWidthMobileNewDesign : styles.productCardWidthMobile) ?? '',
      maxWidthDesktopInRem: (isNewDesign ? styles.productCardWidthNewDesign : styles.productCardWidth) ?? '',
      deviceType: deviceInfo.deviceType,
    })

  const _showDots = isMobile
  const DESKTOP_VISIBLE_SLIDES = isNewDesign ? 6 : 4

  if (!productCards.length) {
    return <SkeletonPlaceholders length={5} />
  }

  if (infiniteScroll) {
    return (
      <InfiniteScrollProductList
        productCards={productCards}
        swiperClassName={swiperClassName}
        gridColCount={gridColCount}
        tracker={tracker}
        productCardDesktopClassName={productCardDesktopClassName}
        productCardExpClassName={productCardExpClassName}
        productCardMobileClassName={productCardMobileClassName}
        wishlistIconOverrideClass={wishlistIconOverrideClass}
        maxWidth={_maxWidth}
        hideReviews={hideProductReviews}
        hideOptions={hideProductOptions}
        hideWishlistIcon={hideWishlistIcon}
        productClickTrackConfig={productClickTrackConfig}
        optionLimit={MAX_NUMBER_OF_COLOR_OPTIONS}
        hideProductName={hideProductName}
        productCardLinkClass={productCardLinkClass}
        hideTags={hideTags}
        skipTagRefresh={skipTagRefresh}
      />
    )
  }

  return (
    <Swiper
      className={classNames(styles.productList, swiperClassName)}
      numberOfVisibleSlides={isDesktop ? DESKTOP_VISIBLE_SLIDES : 1}
      showDots={_showDots}
      // Desktop: variableWidth={false} forces Swiper to calculate equal widths (container÷4), preventing the 5.28px gap
      // that occurs when container (132.5rem) exceeds content (4×33rem=132rem), which would show a ghost image of card 5
      variableWidth={isMobile}
      showNavigator={isDesktop}
      dotsPosition={DOTS_POSITION.BOTTOM}
      tracker={tracker}
      id={id ?? PRODUCT_LIST_SECTION_TWO_ID}
      arrowOnClick={carouselArrowClickTracker}
      trackingMetaData={trackingMetaData}
      prevIconClass={prevIconClass}
      nextIconClass={nextIconClass}
      afterChange={afterChange}
    >
      {isDesktop
        ? productCards.map((productCard, idx) => {
            const _tracker = tracker ? `${tracker}__0__${idx}` : undefined
            // Safe to share between both branches — no legacy-typed unsafe reads. `productClickTrackConfig`
            // (the prop) and `additionalProductTrackingContext`/`wishlistTrackConfig` (caller-supplied
            // functions) are opaque here; only generic context keys are added on top.
            const clickConfigBase = productClickTrackConfig
              ? {
                  ...productClickTrackConfig,
                  context: {
                    ...productClickTrackConfig.context,
                    ...additionalProductTrackingContext(productCard, idx),
                    row: 0,
                    column: idx,
                    productPosition: idx,
                    contentContainerType: productClickTrackConfig.context?.contentContainerType,
                    contentContainerName: productClickTrackConfig.context?.contentContainerName,
                    contentContainerDisplayName: productClickTrackConfig.context?.contentContainerDisplayName,
                    // `tabData` nested in `context` is all SvdProductCard needs — its click analytics
                    // already reads it via `recommendationTabDataFromTrackContext(context)`, so no
                    // separate `tabData` prop is needed on the SVD branch (verified, not assumed).
                    tabData: {
                      ...(productClickTrackConfig.context?.tabData || {}),
                      rec_module_id: tracker ?? PRODUCT_LIST_SECTION_TWO_ID,
                      is_cart_modal: false,
                      product_position: { i: idx, j: 0 },
                    },
                  },
                }
              : undefined

            if (shouldUseSvdCard() && isPlSvdCard(productCard.product)) {
              const svdProduct = mapPlSvdCard(productCard.product as unknown as PlSvdCard)
              return (
                // TODO(productCardLinkClass, productCardImageSectionClass, productPriceClass,
                // disableImageHoverSwap, skipTagRefresh): no SvdProductCardProps equivalent yet.
                <SvdProductCard
                  key={svdProduct.productId}
                  className={classNames(
                    isNewDesign ? styles.productCardNewDesign : styles.productCard,
                    productCardDesktopClassName,
                    idx === productCards.length - 1 && styles.productCard__lastElement,
                  )}
                  product={svdProduct}
                  maxWidth={_maxWidth}
                  hideReviews={hideProductReviews}
                  hideSwatches={hideProductOptions}
                  productViewTracker={productViewTracker}
                  wishlistTrackingConfig={wishlistTrackConfig(productCard, idx)}
                  hideWishlist={hideWishlistIcon}
                  productClickTrackConfig={clickConfigBase}
                  optionLimit={MAX_NUMBER_OF_COLOR_OPTIONS}
                  onShowMoreClick={({ selectedVariant }) => {
                    navigateToPDP({
                      slug: svdProduct.slug,
                      tracker: _tracker,
                      selectedOption: selectedVariant?.displayConfig?.value,
                      router,
                    })
                  }}
                  hideBuyNow={hideBuyNowIcon}
                />
              )
            }

            const initialSelectedVariant = getInitialSelectedVariant({
              cardVariants: productCard.product.cardVariants,
            })
            return (
              <ProductCard
                key={`${productCard.product.productId}_${initialSelectedVariant?.displayConfig.value}`}
                className={classNames(
                  isNewDesign ? styles.productCardNewDesign : styles.productCard,
                  productCardDesktopClassName,
                  idx === productCards.length - 1 && styles.productCard__lastElement,
                )}
                productCard={productCard}
                maxWidth={_maxWidth}
                hideReviews={hideProductReviews}
                hideOptions={hideProductOptions}
                tracker={_tracker}
                productViewTracker={productViewTracker}
                wishlistTrackConfig={wishlistTrackConfig(productCard, idx)}
                skipTagRefresh={skipTagRefresh}
                hideWishlistIcon={hideWishlistIcon}
                productClickTrackConfig={clickConfigBase}
                shouldShowMore
                optionLimit={MAX_NUMBER_OF_COLOR_OPTIONS}
                productPriceClass={productPriceClass}
                onShowMoreClick={({ selectedVariant }) => {
                  navigateToPDP({
                    slug: productCard.product.slug,
                    tracker: _tracker,
                    selectedOption: selectedVariant?.displayConfig.value,
                    router,
                  })
                }}
                productCardLinkClass={productCardLinkClass}
                productCardImageSectionClass={productCardImageSectionClass}
                showMScroll={false}
                tabData={{
                  [RECOMMENDATION_TRACKING_KEYS.REC_ELEMENT_NAME]: String(
                    productClickTrackConfig?.context?.rec_element_name || PRODUCT_LIST_SECTION_TWO_ID,
                  ),
                  [RECOMMENDATION_TRACKING_KEYS.REC_PRODUCT_COUNT]: String(productCards.length),
                  [RECOMMENDATION_TRACKING_KEYS.IS_CART_MODAL]: false,
                  [RECOMMENDATION_TRACKING_KEYS.REC_MODULE_ID]: tracker ?? PRODUCT_LIST_SECTION_TWO_ID,
                  product_position: { i: idx, j: 0 },
                }}
                hideBuyNowIcon={hideBuyNowIcon}
                disableImageHoverSwap={disableImageHoverSwap}
              />
            )
          })
        : productCardChunks.map((productChunk, i) => (
            <div
              key={i}
              className={classNames(
                isNewDesign ? styles.productCardWrapperMobileNewDesign : styles.productCardWrapperMobile,
                productCardWrapperMobileClassName,
                i !== 0 && styles.productCardWrapperMobileChunks,
              )}
            >
              {productChunk.map((productCard, j) => {
                const _tracker = tracker ? `${tracker}__${i}_${j}` : undefined
                const productIndex = productCardChunks.slice(0, i).reduce((acc, ch) => acc + ch.length, 0) + j
                const productPosition = { i: productIndex, j: 0 }
                const clickConfigBase = productClickTrackConfig
                  ? {
                      ...productClickTrackConfig,
                      context: {
                        ...productClickTrackConfig.context,
                        ...additionalProductTrackingContext(productCard, productCards.indexOf(productCard)),
                        row: i,
                        column: j,
                        contentContainerType: productClickTrackConfig.context?.contentContainerType,
                        contentContainerName: productClickTrackConfig.context?.contentContainerName,
                        contentContainerDisplayName: productClickTrackConfig.context?.contentContainerDisplayName,
                        tabData: {
                          ...(productClickTrackConfig.context?.tabData || {}),
                          rec_module_id: productCard.product.productId || 'PRODUCT_LIST_SECTION_TWO',
                          is_cart_modal: false,
                          product_position: productPosition,
                        },
                      },
                    }
                  : undefined

                if (shouldUseSvdCard() && isPlSvdCard(productCard.product)) {
                  const svdProduct = mapPlSvdCard(productCard.product as unknown as PlSvdCard)
                  return (
                    // TODO(wishlistIconClassName, productCardLinkClass, nameAndPriceSectionClass,
                    // productCardImageSectionClass, disableImageHoverSwap): no SvdProductCardProps
                    // equivalent yet.
                    <SvdProductCard
                      key={svdProduct.productId}
                      className={classNames(
                        isNewDesign ? styles.productCardNewDesign : styles.productCard,
                        productCardMobileClassName,
                      )}
                      product={svdProduct}
                      maxWidth={_maxWidth}
                      hideReviews={hideProductReviews}
                      hideSwatches={hideProductOptions}
                      productViewTracker={productViewTracker}
                      wishlistTrackingConfig={wishlistTrackConfig(productCard, productCards.indexOf(productCard))}
                      hideWishlist={hideWishlistIcon}
                      productClickTrackConfig={clickConfigBase}
                      optionLimit={MAX_NUMBER_OF_COLOR_OPTIONS}
                      onShowMoreClick={({ selectedVariant }) => {
                        navigateToPDP({
                          slug: svdProduct.slug,
                          tracker: _tracker,
                          selectedOption: selectedVariant?.displayConfig?.value,
                          router,
                        })
                      }}
                      hideTags={hideTags}
                      hideBuyNow={hideBuyNowIcon}
                    />
                  )
                }

                const initialSelectedVariant = getInitialSelectedVariant({
                  cardVariants: productCard.product.cardVariants,
                })
                return (
                  <ProductCard
                    key={`${productCard.product.productId}_${initialSelectedVariant?.displayConfig.value}`}
                    className={classNames(
                      isNewDesign ? styles.productCardNewDesign : styles.productCard,
                      productCardMobileClassName,
                    )}
                    wishlistIconClassName={classNames(styles.productCard__wishlistIcon, wishlistIconOverrideClass)}
                    productCard={productCard}
                    maxWidth={_maxWidth}
                    hideReviews={hideProductReviews}
                    hideOptions={hideProductOptions}
                    tracker={_tracker}
                    productViewTracker={productViewTracker}
                    wishlistTrackConfig={wishlistTrackConfig(productCard, productCards.indexOf(productCard))}
                    skipTagRefresh={skipTagRefresh}
                    hideWishlistIcon={hideWishlistIcon}
                    productClickTrackConfig={clickConfigBase}
                    shouldShowMore
                    optionLimit={MAX_NUMBER_OF_COLOR_OPTIONS}
                    productPriceClass={productPriceClass}
                    onShowMoreClick={({ selectedVariant }) => {
                      navigateToPDP({
                        slug: productCard.product.slug,
                        tracker: _tracker,
                        selectedOption: selectedVariant?.displayConfig.value,
                        router,
                      })
                    }}
                    productCardLinkClass={productCardLinkClass}
                    hideTags={hideTags}
                    nameAndPriceSectionClass={isNewDesign ? styles.nameAndPriceSection : undefined}
                    productCardImageSectionClass={productCardImageSectionClass}
                    showMScroll={false}
                    hideBuyNowIcon={hideBuyNowIcon}
                    disableImageHoverSwap={disableImageHoverSwap}
                  />
                )
              })}
            </div>
          ))}
    </Swiper>
  )
}
